/**
 * Z_LAYERS — canonical zIndex stack for the Pixi world container.
 *
 * The world container has `sortableChildren=true` (see PixiOverlayHost),
 * so children are re-sorted by zIndex every frame. Insertion order does
 * NOT decide front-to-back — these constants literally determine which
 * things draw on top of which.
 *
 * **Why this exists:** before this module, depths were inline literals
 * scattered across 15+ renderer files (0, 2, 10, 12, 15, 100, 150, 160,
 * 1000, 1001, 2000…). It was trivial for "I'll use depth: 100" to
 * silently land on top of the player without anyone noticing. Multiple
 * unrelated things (VFX subcontainer, target highlight, death overlay,
 * debug label) all collided at zIndex=1000 — their relative order was
 * undefined.
 *
 * **Behavior preservation:** values below match the inline literals as
 * they existed at refactor time (2026-05-13). Migrating to named
 * constants alone changes NO render order. Reordering is a separate
 * step — surgical, one-constant changes once the names exist.
 *
 * **Rule when adding a new renderer:**
 *   1. Identify what it draws (terrain / entity / VFX / overlay / debug)
 *   2. Pick the matching constant below
 *   3. If none fits, ADD a new constant here (not an inline number)
 *
 * UI HUD (React) is screen-space DOM and lives above the canvas via CSS —
 * not affected by these values.
 */

export const Z_LAYERS = {
  // ── Below gameplay ─────────────────────────────────────────────────
  BACKGROUND: -100,            // base backdrop fill — stays the global minimum
  SKY: -98,                    // sky-gradient backplane (PX2) — behind the parallax
  // planes, in front of the base BACKGROUND fill
  CELESTIAL: -97,              // sun/moon body — just above the sky gradient, behind parallax
  CLOUDS: -96,                 // drifting cloud/nebula sky bands — in FRONT of the sun/moon,
  // BEHIND the parallax planes (mountains occlude distant clouds)
  // ── Parallax depth planes (granular multi-plane scenery, PX1) — IN FRONT of the
  //    base BACKGROUND fill, BEHIND ambient atmosphere. far<mid<near (further back =
  //    more negative). Presets place planes per scene via a per-layer `depth`. ──
  PARALLAX_FAR: -95,
  PARALLAX_MID: -80,
  PARALLAX_NEAR: -65,
  // ── Leviathans (drifting mega-silhouettes) — between the far parallax and
  //    the ambient atmosphere band. Far plane is deeper (more distant), near
  //    plane is in front of the far parallax but behind the ambient bed. ──
  LEVIATHAN_FAR: -88,
  LEVIATHAN_NEAR: -72,
  ATMOSPHERE: -50,             // ambient fireflies/god-rays/glow (M3) — above
  // the Phaser tilemap bg, behind Pixi entities

  // ── Terrain (platforms + surface overlays + labels) ────────────────
  ARCHITECTURE: -10,           // authored facades behind every standable edge
  ARCHITECTURE_FIXTURE: -9,    // catalog-mounted fixtures above facades, inside the same architecture container
  PLATFORMS: 0,
  PLATFORM_SURFACES: 1,         // surface overlay on top of platform body
  PLATFORM_LABELS: 2,           // text labels above platforms
  GROUND_DECALS: 2,             // passive aftermath marks (scorch/frost/sigil… — DecalLayer)
  // above platform surfaces (1), below vegetation (3), entity
  // shadows (4), zone floors (10-12), and every sprite — marks
  // read as part of the ground. Shares 2 with PLATFORM_LABELS
  // (sparse edit-time text; acceptable per spec §1.2)
  VEGETATION: 3,                // trees/bushes/grass tufts planted on platforms — above the
  // surface overlay, below entity shadows + all entity sprites
  // (mobs=10, player=100) so characters walk IN FRONT of trunks
  ROPES: 3,                     // climbable rope/ladder visuals — same band as vegetation
  // (above platform surfaces, below entity sprites)
  ENTITY_SHADOW: 4,             // soft contact shadow under each grounded entity —
  // above the platform surface, below ALL entity
  // sprites (mobs=10, player=100). Its own band (not
  // the double-booked 5) so it never fights spawn fx.
  COMPOSITE_PLATFORM_OVERLAY: 5,// composite-platform damage overlays, rings, decorations
  COMPOSITE_PLATFORM_TOP: 6,    // composite-platform top-most decorative graphics
  PROP_LIGHTS: 7,               // authored candles / torches / braziers — above every
  // terrain + prop surface so their flames are not occluded,
  // below hazards, mobs, interactables, and player sprites
  MOB_SPAWN_EFFECTS: 5,         // mob ghost preview / death flash (below mob sprite)

  // ── GM Race course furniture (arc A.6) ─────────────────────────────
  RACE_GROUND_OVERLAY: 3,       // ice slabs / conveyor beds / placed traps — vegetation band,
  // above platform surfaces, below entity shadows + sprites
  RACE_HAZARDS: 9,              // hammer/rock/pad/fan bodies — just below mobs so racers
  // read in front of course furniture
  RACE_PICKUP_GLOW: 11,         // powerup glow disc (behind its icon)
  RACE_PICKUPS: 12,             // powerup icons — projectile band (above mobs)
  RACE_AURA: 98,                // active-effect feet ring — directly below the player sprite
  RACE_FX: 130,                 // one-shot impact rings — above player, below overlay graphics

  // ── Mobs / NPCs ────────────────────────────────────────────────────
  MOB_GROUND_WARNING: 8,        // telegraphed area strike's ground warning (plan 07 D7) —
  // on the floor under mobs and players, above terrain + props
  MOB_AGGRO_INDICATOR: 9,       // pre-aggro exclamation mark
  MOBS: 10,                     // mob sprite
  MOB_ARMOUR_CUE: 11,           // boss rock-armour shell + timer ring, over the mob sprite
  NPCS: 10,                     // NPC sprite (same plane as mobs)

  // ── Projectiles (above mobs, below health bars) ────────────────────
  PROJECTILES: 12,

  // ── Mob HP bars + combat feedback ──────────────────────────────────
  MOB_HEALTH_BARS: 15,
  COMBAT_SWING: 20,             // melee swing arcs
  FLOATING_DAMAGE_TEXT: 200,    // rising damage numbers

  // ── Remote-player owned entities (mounts + pets) ───────────────────
  // Sit above mobs/projectiles but below interactables; original inline
  // depths from dd2ee5a8 were 44 (mount) / 45 (pet). Re-introduced here
  // 2026-05-13 — F.9 migration referenced these without defining them.
  REMOTE_MOUNT: 44,             // other players' mount sprite
  REMOTE_PET: 45,               // other players' pet/summon sprite

  // ── In-world interactables ─────────────────────────────────────────
  INTERACTABLES: 50,            // teleporters, switches, gather nodes

  // ── Player (above mobs but below name tags) ────────────────────────
  PLAYER_SCHEMA_GHOST: 99,      // debug ghost showing schema position
  PLAYER: 100,                  // player sprite
  PARALLAX_FOREGROUND: 120,     // foreground occluder near-plane (atmosphere) — IN FRONT of
  // mobs/player for depth, but BELOW name tags + damage text so
  // those stay readable. Bottom-strip only (never the play space).
  PLAYER_OVERLAY_GRAPHICS: 150, // cooldown rings, ability indicators on the player

  // ── Entity labels (above everything entity-level) ──────────────────
  ENTITY_NAME_TAGS: 160,        // nameplates above heads

  // ── Atmosphere frame top (screen-space grade over the whole scene) ──
  // The colour-grade sits just above the vignette (which draws at a raw 950)
  // so it grades the world INCLUDING the vignette, but stays BELOW ability
  // VFX / overlays (1000) so spell VFX + the HUD read punchy through it.
  COLOR_GRADE: 960,

  // ── Editor (only active in editor mode) ────────────────────────────
  EDITOR_BOUNDARY: 999,
  EDITOR_GRID: 1000,
  EDITOR_CURSOR: 1001,
  EDITOR_OVERLAY_TEXT: 1002,

  // ── Top of world: VFX + overlays ───────────────────────────────────
  // Note: these all sat at zIndex=1000 pre-refactor, with relative
  // order undefined. Spreading them by +100 increments preserves "VFX
  // and overlays are on top" while making subtle ordering explicit.
  // Tweak any of these if order ends up wrong visually.
  VFX: 1000,                    // PixiOverlayHost's vfx subcontainer pin
  TARGET_HIGHLIGHT: 1000,       // CombatRenderer's selected-target ring
  GAME_STATE_OVERLAYS: 1000,    // death overlay + game state graphics
  WORLD_DEBUG_LABEL: 1000,      // PixiDebugLabel — coord overlay, etc.

  // ── Editor overlays (mounted only in editor mode) ──────────────────
  EDITOR_ENTITIES: 1001,
  EDITOR_ENTITY_TEXT: 1002,
  EDITOR_SELECTION_PREVIEW: 1002,
  EDITOR_SELECTION: 1003,

  // ── Dev-only debug at top of stack ─────────────────────────────────
  TELEPORTER_DEBUG_CONNECTIONS: 1100, // TeleporterManager.drawDebugConnections
  DEBUG_OVERLAY_BASE: 1000,           // DebugOverlaySystem.graphics
  DEBUG_OVERLAY_HITBOX: 1001,
  DEBUG_OVERLAY_VELOCITY: 1002,
  DEBUG_OVERLAY_STATE: 1003,
  DEBUG_OVERLAY_TEXT: 1004,
  TERRAIN_DEBUG_OUTLINES: 2000,       // MapRenderer's optional platform outlines (only when DevConfig.logging.terrain)
  INSPECTOR_OVERLAY: 9999,            // InspectorSystem — top of everything
} as const;

export type ZLayerName = keyof typeof Z_LAYERS;
export type ZLayerValue = typeof Z_LAYERS[ZLayerName];
